import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Signature,
  Type,
  Calendar as CalIcon,
  Check,
  User as UserIcon,
  Mail,
  Phone,
  Hash,
  Trash2,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  FileStack,
  Grid3x3,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PdfCanvas from "@/components/PdfCanvas";
import { toast } from "sonner";

const uid = () =>
  (crypto?.randomUUID && crypto.randomUUID()) ||
  Math.random().toString(36).slice(2) + Date.now().toString(36);

const ROLE_COLORS = ["#059669", "#0ea5e9", "#a855f7", "#ef4444", "#f59e0b", "#0d9488"];
const roleColor = (idx) => ROLE_COLORS[idx % ROLE_COLORS.length];

const FIELD_TYPES = [
  { type: "signature", label: "Signature", icon: Signature, w: 180, h: 60 },
  { type: "initials", label: "Initials", icon: Hash, w: 80, h: 40 },
  { type: "full_name", label: "Full name", icon: UserIcon, w: 180, h: 36 },
  { type: "date", label: "Date", icon: CalIcon, w: 130, h: 36 },
  { type: "text", label: "Text", icon: Type, w: 320, h: 60 },
  { type: "email", label: "Email", icon: Mail, w: 180, h: 36 },
  { type: "phone", label: "Phone", icon: Phone, w: 140, h: 36 },
  { type: "checkbox", label: "Checkbox", icon: Check, w: 28, h: 28 },
];

/**
 * Auto-sizing multi-line textarea inside a template field marker.
 * See PrepareDocument.jsx for full rationale — same component, just different testId scheme.
 */
function TextFieldEditor({ f, color, placeholder, testId, onCommit }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !(f.value || "").length) return;
    const prev = el.style.height;
    el.style.height = "auto";
    const measured = Math.max(32, el.scrollHeight);
    el.style.height = prev;
    if (measured > f.height) {
      onCommit(f.value, measured);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    const prev = e.target.style.height;
    e.target.style.height = "auto";
    const measured = Math.max(32, e.target.scrollHeight);
    e.target.style.height = prev;
    onCommit(v, Math.max(measured, f.height));
  };

  return (
    <textarea
      ref={ref}
      value={f.value || ""}
      placeholder={placeholder}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
      spellCheck={false}
      className="inline-edit"
      data-testid={testId}
      style={{
        color,
        resize: "none",
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        lineHeight: 1.4,
        padding: "4px 6px",
      }}
    />
  );
}

export default function PrepareTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [roles, setRoles] = useState(["Signer"]);
  const [activeRole, setActiveRole] = useState(null);
  const [fields, setFields] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [pendingFieldType, setPendingFieldType] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const [mobilePane, setMobilePane] = useState("doc");
  const [showGrid, setShowGrid] = useState(() => {
    try {
      return localStorage.getItem("attestra.grid") === "1";
    } catch {
      return false;
    }
  });
  const GRID = 10;
  const snap = (n) => (showGrid ? Math.round(n / GRID) * GRID : n);
  const toggleGrid = () => {
    setShowGrid((g) => {
      const next = !g;
      try {
        localStorage.setItem("attestra.grid", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/templates/${id}`);
      setTpl(data);
      setTitle(data.title);
      setDescription(data.description || "");
      setCategory(data.category || "");
      const initialRoles = data.roles?.length ? data.roles : ["Signer"];
      setRoles(initialRoles);
      setActiveRole(initialRoles[0]);
      setFields(
        (data.fields || []).map((f) => ({
          ...f,
          recipient_id: f.recipient_id || initialRoles[0],
        })),
      );
      setNumPages(data.page_count || 1);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const addRole = () => {
    const name = prompt("Role name (e.g. Counterparty, Witness)");
    if (!name) return;
    if (roles.includes(name)) return toast.error("That role already exists.");
    setRoles([...roles, name]);
    setActiveRole(name);
    setDirty(true);
  };

  const removeRole = (role) => {
    if (roles.length <= 1) return toast.error("Keep at least one role.");
    setRoles(roles.filter((r) => r !== role));
    setFields(fields.filter((f) => f.recipient_id !== role));
    if (activeRole === role) setActiveRole(roles.filter((r) => r !== role)[0]);
    setDirty(true);
  };

  const removeField = (fid) => {
    setFields(fields.filter((f) => f.id !== fid));
    setSelectedFieldId(null);
    setDirty(true);
  };

  const updateFieldValue = (fid, value) => {
    setFields((prev) => prev.map((f) => (f.id === fid ? { ...f, value } : f)));
    setDirty(true);
  };

  const updateFieldValueAndSize = (fid, value, height) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fid ? { ...f, value, height } : f)),
    );
    setDirty(true);
  };

  const updateFieldSize = (fid, width, height) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fid ? { ...f, width, height } : f)),
    );
    setDirty(true);
  };

  const handleResizeStart = (e, f) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = f.width;
    const startH = f.height;
    const markerEl = e.currentTarget.parentElement;
    const textarea = markerEl?.querySelector("textarea");
    const contentMinH = textarea ? Math.max(32, textarea.scrollHeight) : 24;
    const onMove = (ev) => {
      const newW = Math.max(40, snap(startW + (ev.clientX - startX)));
      const newH = Math.max(contentMinH, snap(startH + (ev.clientY - startY)));
      updateFieldSize(f.id, newW, newH);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const placeField = (e, paletteType) => {
    if (!activeRole) return toast.error("Pick a role first.");
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const spec = FIELD_TYPES.find((f) => f.type === paletteType);
    if (!spec) return;
    const f = {
      id: uid(),
      type: paletteType,
      page: pageNumber,
      x: snap(Math.max(0, x - spec.w / 2)),
      y: snap(Math.max(0, y - spec.h / 2)),
      width: spec.w,
      height: spec.h,
      rendered_viewport_width: 820,
      recipient_id: activeRole,
      required: true,
    };
    setFields([...fields, f]);
    setSelectedFieldId(f.id);
    setPendingFieldType(null);
    setDirty(true);
  };

  const handlePageClick = (e) => {
    if (!pendingFieldType) {
      setSelectedFieldId(null);
      return;
    }
    placeField(e, pendingFieldType);
  };

  // Escape → deselect + cancel pending placement
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedFieldId(null);
        setPendingFieldType(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/x-attestra-field")) {
      e.preventDefault();
    } else if (dragRef.current) {
      e.preventDefault();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const t = e.dataTransfer.getData("application/x-attestra-field");
    if (t) return placeField(e, t);
    if (dragRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newX = e.clientX - rect.left - dragRef.current.offsetX;
      const newY = e.clientY - rect.top - dragRef.current.offsetY;
      const fid = dragRef.current.id;
      setFields((prev) =>
        prev.map((f) =>
          f.id === fid ? { ...f, x: snap(Math.max(0, newX)), y: snap(Math.max(0, newY)) } : f,
        ),
      );
      setDirty(true);
      dragRef.current = null;
    }
  };

  const startFieldDrag = (e, f) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { id: f.id, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
    const img = new Image();
    img.src =
      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciLz4=";
    try {
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {
      /* no-op */
    }
  };

  const replaceFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.patch(`/templates/${id}`, {
          file_name: file.name,
          file_data_url: reader.result,
          page_count: 1,
          fields: [], // wipe field positions when document changes
        });
        toast.success("Document replaced. Re-place fields on the new pages.");
        setFields([]);
        await load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      await api.patch(`/templates/${id}`, {
        title: title.trim(),
        description: description.trim() || null,
        category: category || null,
        roles,
        fields,
      });
      toast.success("Template saved");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-sm text-slate-500">
        Loading template…
      </div>
    );
  }
  if (error || !tpl) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-sm text-red-500">
        {error || "Template not found"}
      </div>
    );
  }

  const visibleFields = fields.filter((f) => f.page === pageNumber);

  return (
    <div
      className="min-h-[100dvh] flex flex-col bg-slate-50"
      data-testid="prepare-template-page"
    >
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6 h-14 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/templates")}
            className="gap-1 text-slate-600 px-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Templates</span>
          </Button>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500 flex items-center gap-1">
              <FileStack className="h-3 w-3" /> Template
            </div>
            <div className="text-xs sm:text-sm font-semibold truncate max-w-[180px] sm:max-w-none">
              {tpl.title}
            </div>
          </div>
          {dirty && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button
            variant={showGrid ? "default" : "outline"}
            size="sm"
            onClick={toggleGrid}
            data-testid="tpl-grid-toggle"
            className={showGrid ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
            title="Toggle alignment grid (snaps to 10px)"
          >
            <Grid3x3 className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Grid</span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => replaceFile(e.target.files?.[0])}
            data-testid="tpl-replace-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            data-testid="tpl-replace-file-btn"
          >
            <RefreshCw className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Replace PDF</span>
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="tpl-save-btn"
          >
            <Save className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{saving ? "Saving…" : "Save"}</span>
          </Button>
        </div>
      </div>

      {/* Mobile pane switcher */}
      <div className="md:hidden bg-white border-b border-slate-200 px-2 py-2 flex items-center gap-1 shrink-0">
        {[
          { v: "fields", label: "Fields" },
          { v: "doc", label: "Document" },
          { v: "meta", label: "Details" },
        ].map((p) => (
          <button
            key={p.v}
            onClick={() => setMobilePane(p.v)}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium ${
              mobilePane === p.v ? "bg-slate-900 text-white" : "text-slate-600"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Workspace */}
      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        {/* Left: palette */}
        <div
          className={`${
            mobilePane === "fields" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-2 bg-white border-r border-slate-200 p-3 overflow-y-auto`}
        >
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Fields
          </div>
          <div className="space-y-1.5">
            {FIELD_TYPES.map((f) => {
              const Icon = f.icon;
              const active = pendingFieldType === f.type;
              const disabled = !activeRole;
              return (
                <button
                  key={f.type}
                  disabled={disabled}
                  draggable={!disabled}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "copy";
                    e.dataTransfer.setData("application/x-attestra-field", f.type);
                  }}
                  onClick={() => setPendingFieldType(active ? null : f.type)}
                  data-testid={`tpl-palette-${f.type}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border cursor-grab active:cursor-grabbing transition ${
                    active
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 hover:bg-slate-50 hover:border-emerald-300"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Icon className="h-4 w-4" />
                  {f.label}
                </button>
              );
            })}
          </div>
          {!activeRole && (
            <div className="mt-3 text-[11px] text-slate-500">Pick a role first →</div>
          )}
        </div>

        {/* Center: PDF + overlay */}
        <div
          className={`${
            mobilePane === "doc" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-7 overflow-auto p-3 sm:p-6`}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-xs font-mono text-slate-600">
              Page {pageNumber} / {numPages}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={showGrid ? "attestra-grid" : ""}
          >
            <PdfCanvas
              fileUrl={tpl.file_data_url}
              pageNumber={pageNumber}
              onLoadPages={(n) => setNumPages(n)}
              onPageClick={handlePageClick}
              renderOverlay={() => (
                <>
                  {visibleFields.map((f) => {
                    const rIdx = roles.findIndex((r) => r === f.recipient_id);
                    const color = roleColor(rIdx >= 0 ? rIdx : 0);
                    const spec = FIELD_TYPES.find((x) => x.type === f.type);
                    const Icon = spec?.icon || Type;
                    const editable = ["text", "date", "full_name", "email", "phone"].includes(
                      f.type,
                    );
                    const isMultiline = f.type === "text";
                    const inputType =
                      f.type === "date" ? "date" : f.type === "email" ? "email" : "text";
                    return (
                      <div
                        key={f.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFieldId(f.id);
                        }}
                        className={`field-marker flex items-center justify-center ${
                          selectedFieldId === f.id ? "ring-2 ring-emerald-500" : ""
                        }`}
                        style={{
                          left: f.x,
                          top: f.y,
                          width: f.width,
                          height: f.height,
                          borderColor: color,
                        }}
                        data-testid={`tpl-field-${f.type}-${f.id}`}
                      >
                        <span
                          className="drag-handle"
                          draggable
                          onDragStart={(e) => startFieldDrag(e, f)}
                          title="Drag to move"
                          data-testid={`tpl-field-drag-${f.id}`}
                          style={{ background: color }}
                        >
                          <svg viewBox="0 0 8 14" width="10" height="14" fill="currentColor" aria-hidden="true">
                            <circle cx="2" cy="2" r="1.2"/><circle cx="6" cy="2" r="1.2"/>
                            <circle cx="2" cy="7" r="1.2"/><circle cx="6" cy="7" r="1.2"/>
                            <circle cx="2" cy="12" r="1.2"/><circle cx="6" cy="12" r="1.2"/>
                          </svg>
                        </span>
                        {isMultiline ? (
                          <RichTextEditor
                            value={f.value || ""}
                            color={color}
                            testId={`tpl-field-input-${f.id}`}
                            onChange={(html) => updateFieldValue(f.id, html)}
                            onHeight={(h) => {
                              if (h && h > (f.height || 0)) {
                                updateFieldSize(f.id, f.width, h + 8);
                              }
                            }}
                          />
                        ) : editable ? (
                          <input
                            type={inputType}
                            value={f.value || ""}
                            placeholder={`${spec?.label} · ${f.recipient_id}`}
                            onChange={(e) => updateFieldValue(f.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.preventDefault()}
                            draggable={false}
                            className="inline-edit"
                            data-testid={`tpl-field-input-${f.id}`}
                            style={{ color }}
                          />
                        ) : (
                          <div className="flex items-center pointer-events-none">
                            <Icon className="h-3 w-3 mr-1" style={{ color }} />
                            <span className="text-[10px] font-mono" style={{ color }}>
                              {spec?.label} · {f.recipient_id}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(f.id);
                          }}
                          className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 hover:bg-red-50 hover:border-red-200"
                          data-testid={`tpl-field-remove-${f.id}`}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                        <span
                          className="resize-handle"
                          onMouseDown={(e) => handleResizeStart(e, f)}
                          onClick={(e) => e.stopPropagation()}
                          title="Drag to resize"
                          data-testid={`tpl-field-resize-${f.id}`}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            />
          </div>
          <div className="mt-3 text-center text-[11px] text-slate-500">
            Drag a field from the left, or click a field type then click the page.
          </div>
        </div>

        {/* Right: meta + roles */}
        <div
          className={`${
            mobilePane === "meta" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-3 bg-white border-l border-slate-200 p-4 overflow-y-auto`}
        >
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Details
          </div>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
                className="mt-1.5 h-10"
                data-testid="tpl-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setDirty(true);
                }}
                rows={3}
                className="mt-1.5"
                data-testid="tpl-desc-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setDirty(true);
                }}
                placeholder="e.g. NDAs"
                className="mt-1.5 h-10"
                data-testid="tpl-cat-input"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Signing roles
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addRole}
                data-testid="tpl-add-role"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-2">
              {roles.map((r, idx) => (
                <div
                  key={r}
                  onClick={() => setActiveRole(r)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer ${
                    activeRole === r
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`tpl-role-${r}`}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: roleColor(idx) }}
                  />
                  <div className="flex-1 text-sm">{r}</div>
                  <span className="text-[11px] font-mono text-slate-400">
                    {fields.filter((f) => f.recipient_id === r).length} fields
                  </span>
                  {roles.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRole(r);
                      }}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 text-xs text-slate-500 leading-relaxed">
            Tip: roles act as placeholders. When you create a document from this
            template, you'll assign real signers (name + email) to each role.
          </div>
        </div>
      </div>
    </div>
  );
}
